// src/pages/AccountPage.jsx
import React, { useState } from 'react';
import { getLocalAccountCode, restoreAccount, setBackupEmail } from '../utils/accountService';

const AccountPage = ({ onBack }) => {
  const [myCode] = useState(getLocalAccountCode());
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const [restoreInput, setRestoreInput] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState(null);

  const [backupEmailInput, setBackupEmailInput] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [backupEmailError, setBackupEmailError] = useState('');
  const [backupEmailSaved, setBackupEmailSaved] = useState(false);

  const handleCopy = () => {
    if (!myCode) return;
    navigator.clipboard.writeText(myCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveBackupEmail = async (e) => {
    e.preventDefault();
    if (!myCode) return;
    setIsSavingEmail(true);
    setBackupEmailError('');
    setBackupEmailSaved(false);
    try {
      await setBackupEmail(myCode, backupEmailInput.trim());
      setBackupEmailSaved(true);
    } catch (err) {
      setBackupEmailError(err.message);
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleRestore = async (e) => {
    e.preventDefault();
    setIsRestoring(true);
    setRestoreError('');
    setRestoreSuccess(null);
    try {
      const data = await restoreAccount(restoreInput);
      setRestoreSuccess(data);
      setRestoreInput('');
    } catch (err) {
      setRestoreError(err.message);
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="page-container">
      <div className="guide-header">
        <h2 className="page-title font-cinzel">🔑 Mi Cuenta</h2>
        <p className="page-subtitle">
          Esta app no pide registro ni contraseña. En su lugar, usa un código secreto de recuperación
          para que puedas acceder a tus cursos comprados y tus lecturas guardadas desde cualquier dispositivo.
        </p>
      </div>

      <div className="zodiac-tool-card account-code-card">
        <h3 className="zodiac-section-title font-cinzel">Tu código de recuperación</h3>
        {myCode ? (
          <>
            <div className="account-code-display">
              <span className="account-code-value">{revealed ? myCode : '•••• •••• ••••'}</span>
              <button className="main-button secondary-button" onClick={() => setRevealed((v) => !v)}>
                {revealed ? 'Ocultar' : 'Mostrar'}
              </button>
              <button className="main-button primary-button" onClick={handleCopy} disabled={!revealed}>
                {copied ? '¡Copiado!' : 'Copiar'}
              </button>
            </div>
            <p className="account-warning">
              ⚠️ Guardalo en un lugar seguro (notas, gestor de contraseñas). Quien tenga este código puede acceder
              a tus compras — nunca lo compartas, ni siquiera con nosotros. Si lo perdés, no hay forma de recuperarlo.
            </p>

            <div className="account-backup-email">
              <h4 className="zodiac-section-subtitle font-cinzel">Email de respaldo (opcional)</h4>
              <p className="zodiac-tool-desc">
                Opcional: dejanos un email de contacto. Esto <strong>no</strong> dispara ningún email automático de
                recuperación. Si perdés tu código, podés escribirnos a{' '}
                <a href="mailto:support@tarotmistico.lat">support@tarotmistico.lat</a> desde ese mismo email y vamos a
                verificar tu identidad manualmente para ayudarte a recuperar el acceso.
              </p>
              <form className="zodiac-tool-form" onSubmit={handleSaveBackupEmail}>
                <input
                  type="email"
                  value={backupEmailInput}
                  onChange={(e) => setBackupEmailInput(e.target.value)}
                  placeholder="tu@email.com"
                  className="guide-search-input"
                  required
                />
                <button type="submit" className="main-button primary-button" disabled={isSavingEmail}>
                  {isSavingEmail ? 'Guardando...' : 'Guardar email de respaldo'}
                </button>
              </form>
              {backupEmailError && <p className="zodiac-horoscope-error">{backupEmailError}</p>}
              {backupEmailSaved && <p className="account-restore-success">✅ Email de respaldo guardado.</p>}
            </div>
          </>
        ) : (
          <p className="zodiac-tool-desc">
            Todavía no tenés un código. Se genera automáticamente la primera vez que comprás un curso o guardás una lectura en tu diario.
          </p>
        )}
      </div>

      <div className="zodiac-tool-card account-code-card">
        <h3 className="zodiac-section-title font-cinzel">Restaurar cuenta en este dispositivo</h3>
        <p className="zodiac-tool-desc">
          ¿Ya tenés un código de otro dispositivo? Ingresalo acá para recuperar tus cursos y lecturas.
        </p>
        <form className="zodiac-tool-form" onSubmit={handleRestore}>
          <input
            type="text"
            value={restoreInput}
            onChange={(e) => setRestoreInput(e.target.value.toUpperCase())}
            placeholder="TM-XXXX-XXXX-XXXX"
            className="guide-search-input"
            required
          />
          <button type="submit" className="main-button primary-button" disabled={isRestoring}>
            {isRestoring ? 'Restaurando...' : 'Restaurar'}
          </button>
        </form>
        {restoreError && <p className="zodiac-horoscope-error">{restoreError}</p>}
        {restoreSuccess && (
          <p className="account-restore-success">
            ✅ Cuenta restaurada: {restoreSuccess.courseIds.length} curso(s) y {restoreSuccess.readings.length} lectura(s) recuperados.
          </p>
        )}
      </div>

      <button onClick={onBack} className="main-button secondary-button back-btn">
        ← Volver al Inicio
      </button>
    </div>
  );
};

export default AccountPage;
